/**
 * Central registry for all icon types used in the application
 * Provides type definitions and registration functions for Ionic, FontAwesome, Heroicons, and Lottie icons
 */

import { addIcons } from 'ionicons';
import {
  add,
  addCircle,
  addCircleOutline,
  addOutline,
  airplaneOutline,
  alertCircle,
  alertCircleOutline,
  appsOutline,
  arrowBackOutline,
  arrowDownOutline,
  arrowForwardOutline,
  arrowUpOutline,
  book,
  bookOutline,
  bulb,
  bulbOutline,
  bugOutline,
  business,
  businessOutline,
  calendar,
  calendarClear,
  calendarClearOutline,
  calendarOutline,
  call,
  callOutline,
  chatbubbleEllipsesOutline,
  chatbubbleOutline,
  checkmark,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkDoneCircleOutline,
  checkmarkOutline,
  chevronBack,
  chevronDown,
  chevronDownOutline,
  chevronForward,
  chevronUp,
  chevronUpOutline,
  close,
  closeCircleOutline,
  closeOutline,
  cloudOutline,
  cloudyNight,
  cloudyNightOutline,
  cogOutline,
  constructOutline,
  create,
  createOutline,
  desktopOutline,
  documentsOutline,
  documentTextOutline,
  downloadOutline,
  easelSharp,
  earthOutline,
  ellipsisHorizontal,
  ellipsisVertical,
  enterOutline,
  flask,
  flaskOutline,
  folderOutline,
  globeOutline,
  grid,
  gridOutline,
  heartOutline,
  helpCircleOutline,
  hourglass,
  hourglassOutline,
  home,
  homeOutline,
  informationCircle,
  informationCircleOutline,
  laptopOutline,
  libraryOutline,
  linkOutline,
  list,
  listOutline,
  lockClosedOutline,
  lockOpenOutline,
  logInOutline,
  logOutOutline,
  mailOpenOutline,
  mailOutline,
  menu,
  menuOutline,
  moon,
  moonOutline,
  newspaperOutline,
  open,
  openOutline,
  peopleOutline,
  peopleCircleOutline,
  pencilOutline,
  person,
  personCircleOutline,
  personOutline,
  refreshOutline,
  remove,
  removeCircleOutline,
  removeOutline,
  reorderThreeOutline,
  rocketOutline,
  schoolOutline,
  search,
  searchOutline,
  settings,
  settingsOutline,
  sparkles,
  sparklesOutline,
  sunny,
  sunnyOutline,
  thumbsUpOutline,
  timeOutline,
  trash,
  trashOutline,
  videocam,
  videocamOutline,
  walkOutline,
  warningOutline,
  wifiOutline,
  cartOutline,
  eyeOutline,
  locate,
  mailUnreadOutline,
} from 'ionicons/icons';

import { faPenToSquare, faHeart, faUser } from '@fortawesome/free-regular-svg-icons';

// ============================================================================
// IONIC ICONS
// ============================================================================

/**
 * Map of all Ionic icons used in the application
 * Key: icon name as used in templates, Value: icon data from ionicons/icons
 */
export const ionicIconsMap: Record<string, string> = {
  add: add,
  'add-circle': addCircle,
  'add-circle-outline': addCircleOutline,
  'add-outline': addOutline,
  'airplane-outline': airplaneOutline,
  'alert-circle': alertCircle,
  'alert-circle-outline': alertCircleOutline,
  'apps-outline': appsOutline,
  'arrow-back-outline': arrowBackOutline,
  'arrow-forward-outline': arrowForwardOutline,
  'arrow-up-outline': arrowUpOutline,
  'arrow-down-outline': arrowDownOutline,
  'bug-outline': bugOutline,
  book: book,
  'book-outline': bookOutline,
  bulb: bulb,
  'bulb-outline': bulbOutline,
  business: business,
  'business-outline': businessOutline,
  calendar: calendar,
  'calendar-clear': calendarClear,
  'calendar-clear-outline': calendarClearOutline,
  'calendar-outline': calendarOutline,
  call: call,
  'call-outline': callOutline,
  'chatbubble-ellipses-outline': chatbubbleEllipsesOutline,
  'chatbubble-outline': chatbubbleOutline,
  checkmark: checkmark,
  'checkmark-circle': checkmarkCircle,
  'checkmark-outline': checkmarkOutline,
  'checkmark-circle-outline': checkmarkCircleOutline,
  'checkmark-done-circle-outline': checkmarkDoneCircleOutline,
  'chevron-back': chevronBack,
  'chevron-forward': chevronForward,
  'chevron-down': chevronDown,
  'chevron-down-outline': chevronDownOutline,
  'chevron-up': chevronUp,
  'chevron-up-outline': chevronUpOutline,
  close: close,
  'close-outline': closeOutline,
  'close-circle-outline': closeCircleOutline,
  'cloud-outline': cloudOutline,
  'cloudy-night': cloudyNight,
  'cloudy-night-outline': cloudyNightOutline,
  create: create,
  'create-outline': createOutline,
  'cog-outline': cogOutline,
  'construct-outline': constructOutline,
  'desktop-outline': desktopOutline,
  'enter-outline': enterOutline,
  'documents-outline': documentsOutline,
  'document-text-outline': documentTextOutline,
  'download-outline': downloadOutline,
  'earth-outline': earthOutline,
  'easel-sharp': easelSharp,
  'ellipsis-horizontal': ellipsisHorizontal,
  'ellipsis-vertical': ellipsisVertical,
  flask: flask,
  'flask-outline': flaskOutline,
  'folder-outline': folderOutline,
  'globe-outline': globeOutline,
  grid: grid,
  'grid-outline': gridOutline,
  'heart-outline': heartOutline,
  'help-circle-outline': helpCircleOutline,
  hourglass: hourglass,
  'hourglass-outline': hourglassOutline,
  home: home,
  'home-outline': homeOutline,
  'information-circle': informationCircle,
  'information-circle-outline': informationCircleOutline,
  'library-outline': libraryOutline,
  'link-outline': linkOutline,
  list: list,
  'list-outline': listOutline,
  'laptop-outline': laptopOutline,
  'school-outline': schoolOutline,
  'cart-outline': cartOutline,
  'eye-outline': eyeOutline,
  locate: locate,
  'newspaper-outline': newspaperOutline,
  'lock-closed-outline': lockClosedOutline,
  'lock-open-outline': lockOpenOutline,
  'log-out-outline': logOutOutline,
  'log-in-outline': logInOutline,
  'mail-open-outline': mailOpenOutline,
  'mail-unread-outline': mailUnreadOutline,
  'mail-outline': mailOutline,
  menu: menu,
  'menu-outline': menuOutline,
  moon: moon,
  'moon-outline': moonOutline,
  open: open,
  'open-outline': openOutline,
  'people-outline': peopleOutline,
  'people-circle-outline': peopleCircleOutline,
  'pencil-outline': pencilOutline,
  person: person,
  'person-outline': personOutline,
  'person-circle-outline': personCircleOutline,
  'reorder-three-outline': reorderThreeOutline,
  'refresh-outline': refreshOutline,
  remove: remove,
  'remove-outline': removeOutline,
  'remove-circle-outline': removeCircleOutline,
  'rocket-outline': rocketOutline,
  settings: settings,
  'settings-outline': settingsOutline,
  sparkles: sparkles,
  'sparkles-outline': sparklesOutline,
  search: search,
  'search-outline': searchOutline,
  sunny: sunny,
  'sunny-outline': sunnyOutline,
  'thumbs-up-outline': thumbsUpOutline,
  'time-outline': timeOutline,
  trash: trash,
  'trash-outline': trashOutline,
  videocam: videocam,
  'videocam-outline': videocamOutline,
  'walk-outline': walkOutline,
  'warning-outline': warningOutline,
  'wifi-outline': wifiOutline,
};

// ============================================================================
// FONTAWESOME ICONS
// ============================================================================

/**
 * Map of FontAwesome icons used in the application
 * Key: icon name as used in templates, Value: FontAwesome icon definition
 */
export const fontAwesomeIconsMap = {
  'fa-pen-to-square': faPenToSquare,
  'fa-heart': faHeart,
  'fa-user': faUser,
} as const;

// ============================================================================
// HEROICONS
// ============================================================================

/**
 * Map of Heroicons used in the application
 * Currently empty - add as needed
 */
export const heroiconsMap: Record<string, any> = {
  // Add Heroicons here when needed
};

// ============================================================================
// LOTTIE ANIMATIONS
// ============================================================================

/**
 * Configuration paths for Lottie animation files
 * Key: icon name, Value: path to JSON animation file
 */
export const lottieIconPaths: Record<string, string> = {
  'settings-icon': '/src/assets/icons/Settings/settings.json',
  'menu-v2': '/src/assets/icons/MenuV2/menuV2.json',
  'calendar-icon': '/src/assets/icons/Calendar/calendar.json',
  'settings-v2-icon': '/src/assets/icons/SettingsV2/settingsV2.json',
  'home-icon': '/src/assets/icons/Home/home.json',
  'info-icon': '/src/assets/icons/Info/info.json',
  'academic-icon': '/src/assets/icons/Academic/academic.json',
  'news-icon': '/src/assets/icons/News/Newspaper.json',
  'newspaper-icon': '/src/assets/icons/News/Newspaper.json',
  'user-avatar': '/src/assets/icons/UserAvatar/userAvatar.json',
  'absence-icon': '/src/assets/icons/Absence/absence.json',
};

/**
 * Configuration metadata for Lottie icons (size, source, etc.)
 */
export const lottieIconConfig: Record<
  string,
  {
    size?: { width?: string; height?: string };
    source?: 'animatedicon.co' | 'custom';
  }
> = {
  'calendar-icon': {
    size: { width: '30px', height: '30px' },
  },
  'home-icon': {
    size: { width: '30px', height: '30px' },
  },
  'academic-icon': {
    source: 'animatedicon.co',
    size: { width: '40px', height: '40px' },
  },
  'news-icon': {
    source: 'animatedicon.co',
    size: { width: '35px', height: '35px' },
  },
  'newspaper-icon': {
    source: 'animatedicon.co',
    size: { width: '35px', height: '35px' },
  },
  'user-avatar': {
    source: 'animatedicon.co',
    size: { width: '34px', height: '34px' },
  },
  'absence-icon': {
    source: 'animatedicon.co',
    size: { width: '35px', height: '35px' },
  },
};

// ============================================================================
// REGISTRATION FUNCTIONS
// ============================================================================

/**
 * Register all Ionic icons with the Ionicons library
 * This should be called once during application initialization
 */
export function registerIonicIcons(): void {
  addIcons(ionicIconsMap);
}

/**
 * Add custom Ionic icons at runtime
 * @param icons - Record of icon names to icon data
 */
export function addCustomIonicIcons(icons: Record<string, string>): void {
  addIcons(icons);
}

/**
 * Register all icons (convenience function)
 * Calls all individual registration functions
 */
export function registerAllIcons(): void {
  registerIonicIcons();
  // Add other registration calls here as needed (FontAwesome, Heroicons, etc.)
}
